import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useVault } from "@/contexts/VaultContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Mic, MicOff, MessageSquare, Sparkles, Send, Trash2, Plus,
  Loader2, Lock, Bot, X, History, Wrench,
} from "lucide-react";

/**
 * Compact icon-row widget that floats top-right of every authenticated
 * page. Two icons:
 *   • Mic → opens drawer in VOICE mode
 *   • Chat bubble → opens drawer in TEXT mode
 *
 * Scope is auto-detected from the URL:
 *   /integrations/xero/*  · /integrations/keypay/*   →  finance
 *   /portal/* · /client/* · client-role user         →  client
 *   everything else for admins                       →  admin
 *
 * Finance scope is gated by the Vault — if locked we show a hint instead
 * of opening the chat.
 */
export default function AssistantWidget() {
  const { user } = useAuth();
  const vault = useVault();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("chat");  // "chat" | "voice"

  const scope = useMemo(() => detectScope(location.pathname, user), [location.pathname, user]);
  if (!user) return null;
  if (!scope) return null;

  const financeLocked = scope === "finance" && !vault.unlocked;

  const openWith = (m) => {
    if (financeLocked) {
      toast.info("Unlock the Finance & Payroll vault first to use this assistant.");
      return;
    }
    setMode(m);
    setOpen(true);
  };

  return (
    <>
      <div className="fixed top-4 right-4 z-40 flex items-center gap-1.5"
        data-testid="assistant-widget">
        <button type="button"
          onClick={() => openWith("voice")}
          title={`Talk to the ${SCOPE_LABEL[scope]} assistant`}
          className="h-9 w-9 rounded-full bg-white border border-zinc-200 grid place-items-center text-zinc-700 hover:border-[#13B5EA] hover:text-[#13B5EA] hover:shadow-sm transition relative"
          data-testid={`assistant-mic-${scope}`}>
          <Mic className="h-4 w-4" />
          {financeLocked && <Lock className="h-2.5 w-2.5 absolute -bottom-0.5 -right-0.5 text-amber-600 bg-white rounded-full" />}
        </button>
        <button type="button"
          onClick={() => openWith("chat")}
          title={`Chat with the ${SCOPE_LABEL[scope]} assistant`}
          className="h-9 rounded-full bg-zinc-900 text-white px-3 inline-flex items-center gap-1.5 text-xs font-display font-bold hover:bg-zinc-800 transition"
          data-testid={`assistant-chat-${scope}`}>
          <Sparkles className="h-3.5 w-3.5" /> Ask AI
          {financeLocked && <Lock className="h-3 w-3 text-amber-300" />}
        </button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right"
          className="w-full sm:max-w-md md:max-w-lg flex flex-col p-0"
          data-testid="assistant-drawer">
          <AssistantPanel
            scope={scope}
            mode={mode}
            setMode={setMode}
            onClose={() => setOpen(false)}
          />
        </SheetContent>
      </Sheet>
    </>
  );
}

const SCOPE_LABEL = {
  admin: "Admin · Client Management",
  finance: "Finance & Payroll",
  client: "Client Portal",
};
const SCOPE_BG = {
  admin: "bg-zinc-900",
  finance: "bg-amber-700",
  client: "bg-[#13B5EA]",
};

function detectScope(pathname, user) {
  const p = pathname || "";
  if (user?.role === "client") return "client";
  if (p.startsWith("/portal") || p.startsWith("/client/")) return "client";
  if (p.startsWith("/integrations/xero") || p.startsWith("/integrations/keypay")
      || p.startsWith("/vault-settings")) return "finance";
  if (user?.role === "admin") return "admin";
  return null;
}

// ── Panel ────────────────────────────────────────────────────────────
function AssistantPanel({ scope, mode, setMode, onClose }) {
  const [conversationId, setConversationId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <>
      <SheetHeader className={`px-4 py-3 ${SCOPE_BG[scope]} text-white`}>
        <div className="flex items-center justify-between">
          <SheetTitle className="text-white text-base font-display font-extrabold inline-flex items-center gap-2">
            <Bot className="h-4 w-4" /> {SCOPE_LABEL[scope]} AI
          </SheetTitle>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setHistoryOpen((h) => !h)}
              className="h-7 w-7 grid place-items-center rounded-sm hover:bg-white/10"
              title="Past conversations"
              data-testid="assistant-history-toggle">
              <History className="h-3.5 w-3.5" />
            </button>
            <button type="button" onClick={onClose}
              className="h-7 w-7 grid place-items-center rounded-sm hover:bg-white/10"
              data-testid="assistant-close">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1 mt-2">
          <ModeChip active={mode === "chat"} icon={MessageSquare} label="Chat"
            onClick={() => setMode("chat")} testid="assistant-mode-chat" />
          <ModeChip active={mode === "voice"} icon={Mic} label="Voice"
            onClick={() => setMode("voice")} testid="assistant-mode-voice" />
        </div>
      </SheetHeader>

      {historyOpen && (
        <HistoryPane scope={scope}
          activeId={conversationId}
          onPick={(id) => { setConversationId(id); setHistoryOpen(false); }}
          onNew={() => { setConversationId(null); setHistoryOpen(false); }}
        />
      )}

      <div className="flex-1 min-h-0 flex flex-col">
        {mode === "chat" ? (
          <ChatPane scope={scope} conversationId={conversationId}
            onConversation={setConversationId} />
        ) : (
          <VoicePane scope={scope} />
        )}
      </div>
    </>
  );
}

function ModeChip({ active, icon: Icon, label, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`h-7 px-2.5 rounded-sm text-[11px] font-display font-bold inline-flex items-center gap-1.5 transition ${
        active ? "bg-white text-zinc-900" : "bg-white/10 text-white hover:bg-white/20"
      }`}>
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

// ── History pane ─────────────────────────────────────────────────────
function HistoryPane({ scope, activeId, onPick, onNew }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assistants/${scope}/conversations`);
      setItems(data || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Delete this conversation?")) return;
    try {
      await api.delete(`/assistants/${scope}/conversations/${id}`);
      if (id === activeId) onNew();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="border-b border-zinc-200 bg-zinc-50 max-h-72 overflow-y-auto"
      data-testid="assistant-history-pane">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200 bg-white">
        <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-zinc-600">
          Past conversations
        </div>
        <Button size="sm" variant="ghost" className="h-7 rounded-sm text-xs"
          onClick={onNew} data-testid="assistant-history-new">
          <Plus className="h-3 w-3 mr-1" /> New chat
        </Button>
      </div>
      {loading ? (
        <div className="p-3 text-xs text-zinc-500"><Loader2 className="h-3 w-3 animate-spin inline" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-3 text-xs italic text-zinc-400">No past conversations.</div>
      ) : items.map((c) => (
        <div key={c.conversation_id}
          className={`flex items-center px-3 py-2 border-b border-zinc-100 last:border-b-0 hover:bg-white cursor-pointer ${
            c.conversation_id === activeId ? "bg-white" : ""
          }`}
          data-testid={`assistant-history-item-${c.conversation_id}`}
          onClick={() => onPick(c.conversation_id)}>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">{c.title}</div>
            <div className="text-[10px] text-zinc-500 font-mono">
              {fmtRel(c.updated_at)}
            </div>
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); del(c.conversation_id); }}
            className="h-6 w-6 grid place-items-center rounded-sm text-zinc-400 hover:text-red-600 hover:bg-red-50"
            data-testid={`assistant-history-delete-${c.conversation_id}`}>
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}

// ── Chat pane ────────────────────────────────────────────────────────
function ChatPane({ scope, conversationId, onConversation }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  // Load messages when conversationId changes (or reset on new chat).
  useEffect(() => {
    if (!conversationId) { setMessages([]); return; }
    api.get(`/assistants/${scope}/conversations/${conversationId}`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => setMessages([]));
  }, [scope, conversationId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: "user", content: input, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post(`/assistants/${scope}/chat`, {
        conversation_id: conversationId,
        message: userMsg.content,
      }, { timeout: 90000 });
      onConversation(data.conversation_id);
      setMessages((m) => [...m, {
        role: "assistant",
        content: data.reply || "(no reply)",
        tool_calls: data.tool_calls,
        created_at: new Date().toISOString(),
      }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Assistant call failed");
      setMessages((m) => [...m, {
        role: "assistant", content: "Sorry, I hit an error. Try again?",
        created_at: new Date().toISOString(),
      }]);
    } finally { setSending(false); }
  };

  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-3"
        data-testid="assistant-chat-messages">
        {messages.length === 0 && !sending && (
          <div className="text-center text-xs text-zinc-400 italic py-8">
            Ask me anything about the {SCOPE_LABEL[scope].toLowerCase()} data.
          </div>
        )}
        {messages.map((m, i) => (
          <ChatMessage key={`${m.role}-${i}-${(m.content || "").slice(0,12)}`} msg={m} />
        ))}
        {sending && (
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Thinking…
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="border-t border-zinc-200 p-3 flex items-end gap-2"
        data-testid="assistant-chat-input-row">
        <textarea
          rows={1}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
          }}
          placeholder={`Message the ${SCOPE_LABEL[scope]} assistant…`}
          className="flex-1 resize-none border border-zinc-200 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#13B5EA] min-h-[40px] max-h-32"
          data-testid="assistant-chat-input"
          disabled={sending}
        />
        <Button onClick={send} disabled={!input.trim() || sending}
          className="rounded-sm h-10 px-3" data-testid="assistant-chat-send">
          <Send className="h-3.5 w-3.5" />
        </Button>
      </div>
    </>
  );
}

function ChatMessage({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[88%] rounded-sm px-3 py-2 text-sm whitespace-pre-wrap ${
        isUser ? "bg-zinc-900 text-white"
               : "bg-zinc-100 text-zinc-900 border border-zinc-200"
      }`}
        data-testid={`assistant-msg-${msg.role}`}>
        {msg.content}
        {(msg.tool_calls || []).length > 0 && (
          <details className="mt-2 text-[10px] opacity-70">
            <summary className="cursor-pointer inline-flex items-center gap-1">
              <Wrench className="h-2.5 w-2.5" />
              {msg.tool_calls.length} tool call{msg.tool_calls.length > 1 ? "s" : ""}
            </summary>
            <div className="mt-1 space-y-1">
              {msg.tool_calls.map((tc, i) => (
                <div key={`${tc.name || "tc"}-${i}`} className="font-mono">
                  · {tc.name}({Object.keys(tc.arguments || {}).join(", ")})
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

// ── Voice pane ────────────────────────────────────────────────────────
function VoicePane({ scope }) {
  const [status, setStatus] = useState("idle");  // idle | connecting | live | error
  const [error, setError] = useState("");
  const [transcript, setTranscript] = useState([]);
  const [muted, setMuted] = useState(false);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const audioRef = useRef(null);

  const stop = useCallback(() => {
    pcRef.current?.close?.(); pcRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;
    setStatus("idle"); setMuted(false);
  }, []);
  useEffect(() => () => stop(), [stop]);

  const start = async () => {
    setStatus("connecting"); setError(""); setTranscript([]);
    try {
      const { data: tok } = await api.post(
        `/assistants/${scope}/voice/session`, {});
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.ontrack = (e) => {
        if (audioRef.current && e.streams?.[0]) {
          audioRef.current.srcObject = e.streams[0];
        }
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      const dc = pc.createDataChannel("oai-events");
      dc.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          if (evt?.type === "response.audio_transcript.done" && evt?.transcript) {
            setTranscript((t) => [...t, { role: "assistant", text: evt.transcript }]);
          }
          if (evt?.type === "conversation.item.input_audio_transcription.completed" && evt?.transcript) {
            setTranscript((t) => [...t, { role: "user", text: evt.transcript }]);
          }
        } catch { /* ignore */ }
      };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const fd = new FormData();
      fd.append("sdp", offer.sdp);
      const r = await fetch(
        `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(tok.model)}`,
        { method: "POST", body: fd,
          headers: { Authorization: `Bearer ${tok.token}` } },
      );
      if (!r.ok) throw new Error(`SDP exchange ${r.status}: ${(await r.text()).slice(0, 200)}`);
      const ans = await r.text();
      await pc.setRemoteDescription({ type: "answer", sdp: ans });
      setStatus("live");
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Voice failed");
      setStatus("error"); stop();
    }
  };

  const toggleMute = () => {
    const next = !muted;
    streamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setMuted(next);
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 flex flex-col"
      data-testid="assistant-voice-pane">
      <audio ref={audioRef} autoPlay className="hidden" />

      {status === "idle" && (
        <div className="text-center my-auto space-y-3">
          <div className="h-20 w-20 mx-auto rounded-full bg-zinc-100 grid place-items-center">
            <Mic className="h-8 w-8 text-zinc-600" />
          </div>
          <div className="text-xs text-zinc-600">
            Voice talks live to OpenAI Realtime with the {SCOPE_LABEL[scope].toLowerCase()} snapshot.
            <br />Use the chat tab for data drill-downs (tool calls).
          </div>
          <Button onClick={start} className="rounded-sm"
            data-testid="assistant-voice-start">
            <Mic className="h-3.5 w-3.5 mr-1.5" /> Start talking
          </Button>
        </div>
      )}

      {status === "connecting" && (
        <div className="text-center my-auto">
          <Loader2 className="h-8 w-8 animate-spin inline" />
          <div className="text-xs text-zinc-500 mt-2">Connecting…</div>
        </div>
      )}

      {status === "live" && (
        <>
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge className="rounded-sm bg-emerald-100 text-emerald-800 border-emerald-200 animate-pulse">
              LIVE
            </Badge>
            <Button size="sm" variant="outline" onClick={toggleMute}
              className="rounded-sm h-8" data-testid="assistant-voice-mute">
              {muted ? <MicOff className="h-3 w-3 mr-1" /> : <Mic className="h-3 w-3 mr-1" />}
              {muted ? "Unmute" : "Mute"}
            </Button>
            <Button size="sm" variant="outline" onClick={stop}
              className="rounded-sm h-8" data-testid="assistant-voice-stop">
              Hang up
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2">
            {transcript.length === 0 && (
              <div className="text-center text-xs text-zinc-400 italic">
                Say something…
              </div>
            )}
            {transcript.map((t, i) => (
              <div key={`${t.role}-${i}-${(t.text || "").slice(0,16)}`}
                className={`flex ${t.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[88%] rounded-sm px-3 py-1.5 text-sm ${
                  t.role === "user" ? "bg-zinc-900 text-white"
                                    : "bg-zinc-100 text-zinc-900 border border-zinc-200"
                }`}>{t.text}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {status === "error" && (
        <div className="text-center my-auto space-y-3">
          <div className="text-xs text-red-700 px-4">{error}</div>
          <Button onClick={start} variant="outline" className="rounded-sm">
            Retry
          </Button>
        </div>
      )}
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────
function fmtRel(iso) {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
